import { useEffect, useState } from 'react';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';

/**
 * Banner statt Modal (GDD Mobile-Härtung): Die Oberfläche bleibt sichtbar und
 * bedienbar, während im Hintergrund neu verbunden wird.
 *
 * Zwei Zustände sind Sackgassen und brauchen eine Handlung statt eines
 * Wartehinweises: veraltetes Protokoll (neu laden) und ein zweiter Tab, der die
 * Verbindung übernommen hat (hier fortsetzen).
 *
 * Das Ein- und Ausblenden läuft über `open`/CSS statt über `AnimatePresence`:
 * Bliebe das Element nach einer nicht abgeschlossenen Ausblend-Animation
 * stehen, klebte über der wiederhergestellten Verbindung dauerhaft eine leere
 * Leiste — ausgerechnet als Beweis dafür, dass alles wieder läuft.
 */
export function ConnectionBanner() {
  const status = useAppStore((s) => s.connectionStatus);
  const nextRetryAt = useAppStore((s) => s.nextRetryAt);
  const pendingActions = useAppStore((s) => s.pendingActions);

  const visible = status !== 'connected' && status !== 'connecting';
  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex animate-fade-in flex-col items-center gap-1 bg-surface-2 px-4 py-2 text-sm text-fg-muted"
    >
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="h-2 w-2 animate-pulse-soft rounded-full bg-accent" aria-hidden="true" />
        <BannerLabel status={status} nextRetryAt={nextRetryAt} />
        <BannerAction status={status} />
      </div>
      {pendingActions > 0 && (
        <span className="text-xs text-fg-faint">{de.connection.pending(pendingActions)}</span>
      )}
    </div>
  );
}

function BannerLabel({
  status,
  nextRetryAt,
}: {
  status: string;
  nextRetryAt: number | null;
}) {
  const countdown = useCountdown(nextRetryAt);

  switch (status) {
    case 'outdated':
      return <span>{de.connection.outdated}</span>;
    case 'takenOver':
      return <span>{de.connection.takenOver}</span>;
    case 'offline':
      return (
        <span>
          {de.connection.offline}
          {countdown !== null && ` · ${de.connection.retryIn(countdown)}`}
        </span>
      );
    default:
      return (
        <span>
          {de.connection.reconnecting}
          {countdown !== null && ` · ${de.connection.retryIn(countdown)}`}
        </span>
      );
  }
}

function BannerAction({ status }: { status: string }) {
  if (status === 'outdated') {
    return (
      <button type="button" className="underline underline-offset-2" onClick={() => location.reload()}>
        {de.connection.reload}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="underline underline-offset-2"
      onClick={() => wsClient.resume()}
    >
      {status === 'takenOver' ? de.connection.resumeHere : de.connection.retryNow}
    </button>
  );
}

/** Sekunden bis zum nächsten Versuch — damit das Warten sichtbar endlich ist. */
function useCountdown(target: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target === null) return;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [target]);

  if (target === null) return null;
  return Math.max(0, Math.ceil((target - now) / 1000));
}
